import React, { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// mui
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
// component
import { useWeb3 } from '@/contexts/web3Provider';
import networkList, { resNetworkList, SUPPORTED_CHAINS } from '@/component/wallet/NetworkList';
// lib
import { inter } from '@/theme';
import { SFPalette } from '@/lib/color';
import { ShortenAddr } from '@/lib/Shorten';
import Qrcode from '@/lib/Qrcode/Qrcode';
import { copy, copys } from '@/lib/util';
import { ethToWei } from '@/lib/web3/utils';
import { POLYGON_MAIN } from '@/lib/common/name-space';

type DrawerMode = 'receive' | 'paymentRequest';

function DownloadDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { address } = useWeb3();

  // 결제 요청 모드 상태
  const [mode, setMode] = useState<DrawerMode>('receive');
  const [amount, setAmount] = useState('');
  const [selectedChain, setSelectedChain] = useState<number>(POLYGON_MAIN);
  const [selectedToken, setSelectedToken] = useState<resNetworkList | null>(null);
  const [tokens, setTokens] = useState<resNetworkList[]>([]);

  // Drawer가 닫힐 때 상태 초기화
  useEffect(() => {
    if (!open) {
      setMode('receive');
      setAmount('');
      setSelectedChain(POLYGON_MAIN);
      setSelectedToken(null);
      setTokens([]);
    }
  }, [open]);

  // 체인 변경 시 토큰 목록 로드
  useEffect(() => {
    if (mode === 'paymentRequest') {
      (async () => {
        const tokenList = await networkList(selectedChain);
        setTokens(tokenList);
        if (tokenList.length > 0) {
          setSelectedToken(tokenList[0]);
        }
      })();
    }
  }, [selectedChain, mode]);

  // 결제 요청 URL 생성
  const generatePaymentRequestUrl = (): string => {
    if (!address || !selectedToken || !amount) return '';

    try {
      const amountInWei = ethToWei(amount);
      const baseUrl = window.location.origin;
      const path = 'sendForm';
      return `${baseUrl}/outLink@${path}@${address}@${amountInWei}@${selectedToken.symbol}@${selectedChain}`;
    } catch (error) {
      console.error('URL 생성 오류:', error);
      return '';
    }
  };

  // 체인 변경 핸들러
  const handleChainChange = (event: SelectChangeEvent<number>) => {
    setSelectedChain(Number(event.target.value));
  };

  // 토큰 변경 핸들러
  const handleTokenChange = (event: SelectChangeEvent<string>) => {
    const token = tokens.find((t) => t.symbol === event.target.value);
    setSelectedToken(token || null);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          p: 3,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '90vh',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <Close />
          </IconButton>
        </Box>

        <Box>
          {/* 모드 토글 버튼 */}
          <Stack direction="row" gap={1} mb={3}>
            <Button
              variant={mode === 'receive' ? 'contained' : 'outlined'}
              onClick={() => setMode('receive')}
              fullWidth
              size="medium"
            >
              {t('validation.receive')}
            </Button>
            {/* <Button
              variant={mode === 'paymentRequest' ? 'contained' : 'outlined'}
              onClick={() => setMode('paymentRequest')}
              fullWidth
              size="medium"
            >
              결제 요청
            </Button> */}
          </Stack>

          {/* 결제 요청 모드: 체인/토큰/금액 입력 폼 */}
          {mode === 'paymentRequest' && (
            <Box mb={2}>
              {/* 체인 선택 */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>체인</InputLabel>
                <Select value={selectedChain} onChange={handleChainChange} label="체인">
                  {SUPPORTED_CHAINS.map((chain) => (
                    <MenuItem key={chain.id} value={chain.id}>
                      {chain.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 토큰 선택 */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>토큰</InputLabel>
                <Select
                  value={selectedToken?.symbol || ''}
                  onChange={handleTokenChange}
                  label="토큰"
                  disabled={tokens.length === 0}
                >
                  {tokens.map((token) => (
                    <MenuItem key={token.symbol} value={token.symbol}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center' }}>{token.icon}</Box>
                        {token.symbol}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 금액 입력 */}
              <TextField
                fullWidth
                size="small"
                label="금액"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{selectedToken?.symbol || ''}</InputAdornment>,
                }}
                placeholder="0"
              />
            </Box>
          )}

          <Box textAlign="center">
            {/* QR Code - 모드에 따라 다른 값 표시 */}
            <Qrcode value={mode === 'paymentRequest' ? generatePaymentRequestUrl() || address || '' : address || ''} />
            <Typography variant="subtitle2" fontWeight="700" color={SFPalette.grey2} mb={3.5}>
              {mode === 'paymentRequest' ? '결제 요청 QR 코드를 스캔하세요' : t('message.scanYourReceiveAddress')}
            </Typography>
          </Box>

          <Stack direction="row" gap={2}>
            {mode === 'receive' ? (
              /* 받기 모드: 주소 복사 버튼 */
              <Button
                id="copyButton"
                ref={buttonRef}
                onClick={() => {
                  copys(String(address));
                  toast.success(`${t('toast.copyYourWallet')}`);
                }}
                fullWidth
                size="large"
                variant="contained"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  fontFamily={inter.style.fontFamily}
                  textTransform="none"
                >
                  {ShortenAddr(address ?? '')} {t('button.copyWallet')}
                </Typography>
              </Button>
            ) : (
              /* 결제 요청 모드: 링크 복사 버튼 */
              <Button
                onClick={() => {
                  const url = generatePaymentRequestUrl();
                  if (!url) {
                    toast.error('금액을 입력해 주세요.');
                    return;
                  }
                  copys(url);
                  toast.success('결제 요청 링크가 복사되었습니다.');
                }}
                fullWidth
                size="large"
                variant="contained"
                disabled={!amount || !selectedToken}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  fontFamily={inter.style.fontFamily}
                  textTransform="none"
                >
                  결제 요청 링크 복사
                </Typography>
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}

export default DownloadDrawer;
